import moment from 'moment';
import React, { useCallback, useEffect, useState } from 'react';
import InputMask from 'react-input-mask';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Grid,
  IconButton,
  Paper,
  TableContainer,
  TextField,
  Tooltip,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import TaskAltIcon from '@mui/icons-material/TaskAlt';
import FolderZipIcon from '@mui/icons-material/FolderZip';
import UnpublishedIcon from '@mui/icons-material/Unpublished';
import PictureAsPdfIcon from '@mui/icons-material/PictureAsPdf';
import ArticleIcon from '@mui/icons-material/Article';
import ErrorOutlineIcon from '@mui/icons-material/ErrorOutline';
import JSZip from 'jszip';
import { saveAs } from 'file-saver';

import IPage from '../../../interfaces/IPage';
import IInvoice from '../../../interfaces/IInvoice';
import { useCustomerPortalInvoiceService } from '../../../services/useCustomerPortalInvoiceService';
import { useToastr } from '../../../hooks/useToastr';
import CustomTablePagination from '../../../components/table/CustomTablePagination/CustomTablePagination';
import CustomTableNoContent from '../../../components/table/CustomTableNoContent/CustomTableNoContent';
import { BackdropCustom } from '../../../components/backdrop/Backdrop';
import { PageCard } from '../../../components/page-card/PageCard';
import { Button } from '../../../components/button/Button';
import { ButtonGroup } from '../../../components/button-group/ButtonGroup';
import IFormError from '../../../interfaces/IFormError';
import {
  ActionArea,
  BodyCard,
  BodyCardInfo,
  Content,
  FileDownloader,
  HeaderCard,
  Input,
  RightArea,
} from './CustomerPortalInvoices.styles';

interface IQueryParams {
  currentPage: number;
  perPage: number;
  orderField: string;
  orderDirection: string;
  number: string;
  initialDate: string;
  finalDate: string;
}

const CustomerPortalInvoices: React.FC<IPage> = ({ title }) => {
  window.document.title = title;

  const { listInvoices } = useCustomerPortalInvoiceService();
  const toastr = useToastr();

  const [invoices, setInvoices] = useState<IInvoice[]>([]);
  const [invoicesSelecteds, setInvoicesSelecteds] = useState<IInvoice[]>([]);

  const [perPage, setPerPage] = useState(10);
  const [currentPage, setCurrentPage] = useState(0);
  const [orderField] = useState('emissionDate');
  const [orderDirection] = useState<'desc' | 'asc'>('desc');
  const [totalRows, setTotalRow] = useState(0);
  const [number, setNumber] = useState('');
  const [initialDate, setInitialDate] = useState('');
  const [finalDate, setFinalDate] = useState('');
  const [loading, setLoading] = useState(false);
  const [formErrors, setFormErros] = useState<IFormError>({});
  const [showFilters, setShowFilters] = useState(false);

  const [os] = useState(() => {
    const toMatch = [/Macintosh/i, /webOS/i, /iPhone/i, /iPad/i, /iPod/i, /BlackBerry/i, /Windows Phone/i];
    return toMatch.some(toMatchItem => navigator.userAgent.match(toMatchItem));
  });

  const hasValidFile = (invoice: IInvoice) =>
    (invoice.pdf && invoice.pdf.length > 50) || (invoice.xml && invoice.xml.length > 50);

  const handleSelectItem = (invoice: IInvoice) => {
    if (!hasValidFile(invoice)) return;

    if (invoicesSelecteds.some(item => item.id === invoice.id)) {
      setInvoicesSelecteds(invoicesSelecteds.filter(item => item.id !== invoice.id));
    } else {
      setInvoicesSelecteds([...invoicesSelecteds, invoice]);
    }
  };

  const handleSelectAll = useCallback(() => {
    setInvoicesSelecteds(invoices.filter(hasValidFile));
  }, [invoices]);

  const downloadFile = (base64: string, filename: string, mime: string) => {
    const byteCharacters = atob(base64);
    const byteNumbers = new Array(byteCharacters.length);
    for (let i = 0; i < byteCharacters.length; i++) {
      byteNumbers[i] = byteCharacters.charCodeAt(i);
    }
    const blob = new Blob([new Uint8Array(byteNumbers)], { type: mime });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    if (os) {
      link.target = '_blank';
    } else {
      link.download = filename;
    }
    link.click();
    URL.revokeObjectURL(url);
  };

  const downloadPDF = (invoice: IInvoice) => {
    if (invoice.pdf && invoice.pdf.length > 50) {
      downloadFile(invoice.pdf, `nf-${invoice.number}.pdf`, 'application/pdf');
    }
  };

  const downloadXML = (invoice: IInvoice) => {
    if (invoice.xml && invoice.xml.length > 50) {
      downloadFile(invoice.xml, `nf-${invoice.number}.xml`, 'application/xml');
    }
  };

  const downloadCompactAllFiles = () => {
    if (invoicesSelecteds.length === 0) {
      toastr.info('Nenhum arquivo selecionado');
      return;
    }

    const zip = new JSZip();

    invoicesSelecteds.forEach(invoice => {
      const addToZip = (base64: string, filename: string, mime: string) => {
        const byteCharacters = atob(base64);
        const byteNumbers = new Array(byteCharacters.length);
        for (let i = 0; i < byteCharacters.length; i++) {
          byteNumbers[i] = byteCharacters.charCodeAt(i);
        }
        zip.file(filename, new Blob([new Uint8Array(byteNumbers)], { type: mime }));
      };

      if (invoice.pdf && invoice.pdf.length > 50) {
        addToZip(invoice.pdf, `nf-${invoice.number}.pdf`, 'application/pdf');
      }
      if (invoice.xml && invoice.xml.length > 50) {
        addToZip(invoice.xml, `nf-${invoice.number}.xml`, 'application/xml');
      }
    });

    zip.generateAsync({ type: 'blob' }).then(content => {
      saveAs(content, 'notas-fiscais.zip');
    });
  };

  const handleListInvoices = useCallback(
    async ({ currentPage, perPage, orderField, orderDirection, number, initialDate, finalDate }: IQueryParams) => {
      setFormErros({});
      setInvoicesSelecteds([]);

      let queryParams = `perPage=${perPage}&currentPage=${currentPage + 1}&orderField=${orderField}&orderDirection=${orderDirection}`;

      if (number) {
        queryParams += `&number=${number}`;
      }

      if (initialDate) {
        if (initialDate.length < 10 || !moment(initialDate, 'DD/MM/YYYY').isValid()) {
          setFormErros({ initialDate: 'Data inicial inválida' });
          return;
        }
        queryParams += `&initialEmissionDate=${moment(initialDate, 'DD/MM/YYYY').format('YYYY-MM-DD')}`;
      }

      if (finalDate) {
        if (finalDate.length < 10 || !moment(finalDate, 'DD/MM/YYYY').isValid()) {
          setFormErros({ finalDate: 'Data final inválida' });
          return;
        }
        queryParams += `&finalEmissionDate=${moment(finalDate, 'DD/MM/YYYY').format('YYYY-MM-DD')}`;
      }

      setLoading(true);
      await listInvoices(queryParams)
        .then(response => {
          if (response.data.length > 0) {
            setInvoices(response.data);
            setTotalRow(response.totalRows);
          } else {
            setInvoices([]);
            setTotalRow(0);
            setCurrentPage(0);
          }
        })
        .catch(error => {
          toastr.error(error?.message || 'Contate a equipe de suporte');
        })
        .finally(() => {
          setLoading(false);
        });
    },
    [toastr, listInvoices],
  );

  const clearFilters = useCallback(() => {
    setNumber('');
    setInitialDate('');
    setFinalDate('');
    setCurrentPage(0);
    handleListInvoices({ currentPage: 0, perPage, orderField, orderDirection, number: '', initialDate: '', finalDate: '' });
  }, [perPage, orderField, orderDirection, handleListInvoices]);

  useEffect(() => {
    handleListInvoices({ currentPage, perPage, orderField, orderDirection, number, initialDate, finalDate });
  }, [currentPage, perPage, orderField, orderDirection]);

  return (
    <>
      <Paper sx={{ margin: '16px' }} elevation={6}>
        <Accordion elevation={0}>
          <AccordionSummary
            expandIcon={<ExpandMoreIcon />}
            aria-controls="panel1-content"
            id="panel1-header"
            sx={{ marginLeft: '2px' }}
            onClick={() => setShowFilters(!showFilters)}
          >
            Filtros
          </AccordionSummary>
          <AccordionDetails sx={{ marginBottom: '24px' }}>
            <Grid container spacing={2}>
              <Grid item xs={12} md={4} sm={4}>
                <TextField
                  sx={{ width: '100%' }}
                  size="small"
                  label="Número da NF"
                  value={number}
                  onChange={e => setNumber(e.target.value)}
                  autoComplete="off"
                />
              </Grid>

              <Grid item xs={12} md={4} sm={4}>
                <Input>
                  <InputMask
                    mask={'99/99/9999'}
                    value={initialDate}
                    onChange={e => setInitialDate(e.target.value)}
                    maskPlaceholder={null}
                  >
                    <TextField
                      fullWidth
                      size="small"
                      label="Data Inicial de Emissão"
                      error={!!formErrors.initialDate}
                      helperText={formErrors.initialDate}
                    />
                  </InputMask>
                </Input>
              </Grid>

              <Grid item xs={12} md={4} sm={4}>
                <Input>
                  <InputMask
                    mask={'99/99/9999'}
                    value={finalDate}
                    onChange={e => setFinalDate(e.target.value)}
                    maskPlaceholder={null}
                  >
                    <TextField
                      fullWidth
                      size="small"
                      label="Data Final de Emissão"
                      error={!!formErrors.finalDate}
                      helperText={formErrors.finalDate}
                    />
                  </InputMask>
                </Input>
              </Grid>
            </Grid>

            <ButtonGroup>
              <Button
                variant="outlined"
                size="small"
                onClick={clearFilters}
              >
                Limpar
              </Button>
              <Button
                variant="outlined"
                size="small"
                color="success"
                onClick={() => handleListInvoices({ currentPage, perPage, orderField, orderDirection, number, initialDate, finalDate })}
              >
                Confirmar
              </Button>
            </ButtonGroup>
          </AccordionDetails>
        </Accordion>
      </Paper>

      <PageCard>
        <FileDownloader>
          <Button onClick={downloadCompactAllFiles} size="small" variant="outlined">
            <FolderZipIcon fontSize="small" />
            <span>Compactar selecionados</span>
          </Button>

          <Button onClick={handleSelectAll} size="small" variant="outlined">
            <TaskAltIcon fontSize="small" />
            <span>Selecionar todos</span>
          </Button>

          <Button onClick={() => setInvoicesSelecteds([])} size="small" variant="outlined">
            <UnpublishedIcon fontSize="small" />
            <span>Desmarcar todos</span>
          </Button>
        </FileDownloader>

        <TableContainer>
          {invoices.length > 0 ? (
            <>
              {invoices.map(invoice => (
                <Content key={invoice.id}>
                  <div>
                    <HeaderCard>
                      <strong className="invoice-number">NF {invoice.number}</strong>
                    </HeaderCard>
                    <BodyCard>
                      <BodyCardInfo>
                        <span>{moment(invoice.emissionDate).format('DD/MM/YYYY')}</span>
                        <span>
                          <strong>
                            {`R$ ${(invoice.netValue ?? 0).toLocaleString('pt-BR', {
                              style: 'decimal',
                              minimumFractionDigits: 2,
                            })}`}
                          </strong>
                        </span>
                      </BodyCardInfo>
                    </BodyCard>
                  </div>

                  <RightArea>
                    {hasValidFile(invoice) ? (
                      <ActionArea selected={invoicesSelecteds.some(item => item.id === invoice.id)}>
                        <IconButton id="select-button" onClick={() => handleSelectItem(invoice)} size="large">
                          <TaskAltIcon fontSize="large" />
                        </IconButton>

                        {invoice.pdf && invoice.pdf.length > 50 && (
                          <Tooltip title="Download PDF" arrow placement="top">
                            <IconButton onClick={() => downloadPDF(invoice)} size="large" color="error">
                              <PictureAsPdfIcon fontSize="large" />
                            </IconButton>
                          </Tooltip>
                        )}

                        {invoice.xml && invoice.xml.length > 50 && (
                          <Tooltip title="Download XML" arrow placement="top">
                            <IconButton onClick={() => downloadXML(invoice)} size="large" color="info">
                              <ArticleIcon fontSize="large" />
                            </IconButton>
                          </Tooltip>
                        )}
                      </ActionArea>
                    ) : (
                      <ActionArea selected={false}>
                        <Tooltip title="Arquivos indisponíveis para download." arrow placement="top">
                          <ErrorOutlineIcon fontSize="large" color="error" style={{ cursor: 'pointer', padding: '12px' }} />
                        </Tooltip>
                      </ActionArea>
                    )}
                  </RightArea>
                </Content>
              ))}
            </>
          ) : (
            <CustomTableNoContent text="Nenhuma nota fiscal para listar..." />
          )}
        </TableContainer>

        <CustomTablePagination
          totalRows={totalRows}
          currentPage={currentPage}
          perPage={perPage}
          handleRequest={() =>
            handleListInvoices({ currentPage, perPage, orderField, orderDirection, number, initialDate, finalDate })
          }
          setPerPage={setPerPage}
          setCurrentPage={setCurrentPage}
          orderField={orderField}
          orderDirection={orderDirection}
        />
      </PageCard>

      {loading && <BackdropCustom />}
    </>
  );
};

export { CustomerPortalInvoices };
